'use client'
import { FieldLabel } from '@/components/common/text-field/components'
import { Box, styled, TextareaAutosize, useTheme } from '@mui/material'
import type { TextFieldProps } from '@mui/material/TextField'
import { Controller, useFormContext } from 'react-hook-form'

// ----------------------------------------------------------------------

export type RHFTextareaProps = Omit<TextFieldProps, 'multiline'> & {
  name: string
  rows?: number
  minRows?: number
  maxRows?: number
}

export function RHFTextarea({
  name,
  helperText,
  label,
  required,
  rows = 3,
  minRows,
  maxRows,
  placeholder,
  ...other
}: RHFTextareaProps) {
  const { control } = useFormContext()
  const theme = useTheme()

  return (
    <Box sx={{ width: '100%' }}>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Box>
            {label && <FieldLabel label={label} required={required} />}
            <TextareaAutosize
              {...field}
              minRows={minRows || rows}
              maxRows={maxRows}
              placeholder={placeholder}
              style={{
                width: '100%',
                fontFamily: 'inherit',
                fontSize: '1rem',
                padding: '12px',
                borderRadius: '8px',
                border: error ? '1px solid #d32f2f' : '1px solid #e0e0e0',
                outline: 'none',
                resize: 'vertical',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => {
                if (!error) {
                  e.target.style.borderColor = theme.palette.primary.main
                }
              }}
              onBlur={(e) => {
                if (!error) {
                  e.target.style.borderColor = '#e0e0e0'
                }
                field.onBlur()
              }}
            />
            {(error?.message || helperText) && (
              <Box
                sx={{
                  mt: 0.5,
                  fontSize: '12px',
                  color: error ? 'error.main' : 'text.secondary',
                }}
              >
                {error?.message || helperText}
              </Box>
            )}
          </Box>
        )}
      />
    </Box>
  )
}
